import { useState } from "react";
import { DocumentForm } from "@/components/DocumentForm";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Plus, Minus, Pencil, MoveVertical } from "lucide-react";

const SIGNING = { both: "Participant/guardian + office sign", office: "Office signs only", none: "No signatures required" };
const KIND_ICON = { added: Plus, removed: Minus, changed: Pencil, moved: MoveVertical };
const KIND_CLASS = { added: "text-emerald-600", removed: "text-rose-600", changed: "text-amber-600", moved: "text-sky-600" };

// Interactive preview of what the AI built (new forms) or proposes (existing forms).
// proposal = { template, base?, diff?, warnings, mode }
export function DocAiPreview({ proposal }) {
  const isEdit = proposal.mode === "edit";
  const [view, setView] = useState("after");
  const [showAll, setShowAll] = useState(false);
  const [values, setValues] = useState({});
  const tmpl = view === "before" && isEdit ? proposal.base : proposal.template;
  const marks = isEdit ? proposal.diff?.marks : null;
  const counts = proposal.diff?.counts || {};
  const changes = proposal.diff?.changes || [];

  return (
    <div className="flex flex-col md:flex-row gap-0 md:gap-4" data-testid="ai-preview">
      <div className="md:w-[62%] min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          {isEdit ? (
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" role="tablist">
              {["before", "after"].map((v) => (
                <button key={v} onClick={() => setView(v)} role="tab" aria-selected={view === v} data-testid={`ai-preview-${v}`}
                  className={`px-3 py-1.5 capitalize ${view === v ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {v === "before" ? "Before (current)" : "After (proposed)"}
                </button>
              ))}
            </div>
          ) : <div className="text-xs font-medium text-slate-600">Preview — what staff will see</div>}
          <div className="flex items-center gap-2">
            <Switch id="ai-show-all" checked={showAll} onCheckedChange={setShowAll} data-testid="ai-preview-showall" />
            <Label htmlFor="ai-show-all" className="text-[11px] text-slate-600">Show conditional fields</Label>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 overflow-auto" style={{ height: "56vh" }} data-testid="ai-preview-form">
          <div className="mb-3">
            <div className="text-base font-semibold text-slate-800">{tmpl.title}</div>
            {tmpl.description && <div className="text-xs text-slate-500">{tmpl.description}</div>}
            <div className="text-[10px] text-slate-400 mt-0.5">{SIGNING[tmpl.signing_mode] || tmpl.signing_mode}</div>
          </div>
          <DocumentForm schema={tmpl.sections} values={values} onChange={setValues} marks={marks} showAll={showAll} />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">You can fill this in to try out the conditions. Nothing here is saved.</p>
      </div>

      <div className="md:w-[38%] mt-3 md:mt-0 min-w-0">
        {proposal.warnings?.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 mb-3 text-[11px] text-amber-800" data-testid="ai-preview-warnings">
            <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle className="h-3.5 w-3.5" />Check before applying</div>
            <ul className="list-disc pl-4 space-y-0.5">{proposal.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
        {isEdit ? (
          <div data-testid="ai-preview-changes">
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              {changes.length === 0 ? "No changes" : `${changes.length} change${changes.length === 1 ? "" : "s"}`}
              <span className="font-normal text-slate-400">
                {counts.added ? ` · ${counts.added} added` : ""}{counts.changed ? ` · ${counts.changed} changed` : ""}
                {counts.moved ? ` · ${counts.moved} moved` : ""}{counts.removed ? ` · ${counts.removed} removed` : ""}
              </span>
            </div>
            <div className="overflow-auto space-y-1.5 pr-1" style={{ maxHeight: "50vh" }}>
              {changes.length === 0 && <p className="text-xs text-slate-400">The AI's proposal is identical to the current form. Go back and describe what to change.</p>}
              {changes.map((c, i) => {
                const Icon = KIND_ICON[c.kind] || Pencil;
                return (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-2 text-[11px]">
                    <div className={`flex items-center gap-1.5 font-medium ${KIND_CLASS[c.kind]}`}>
                      <Icon className="h-3 w-3 shrink-0" />
                      <span className="capitalize">{c.kind}</span>
                      <span className="text-slate-700 truncate">{c.label}</span>
                    </div>
                    <div className="text-slate-400 mt-0.5">{c.scope === "form" ? "Form setting" : c.scope === "section" ? "Section" : `in ${c.section}`}{c.detail ? ` — ${c.detail}` : ""}</div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Applying loads this into the editor — nothing is saved until you press Save. Documents already created keep their current version.</p>
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 space-y-1">
            <div className="text-xs font-semibold text-slate-700">{tmpl.sections.length} sections · {tmpl.sections.reduce((n, s) => n + (s.fields || []).length, 0)} fields</div>
            <p>Check the form on the left. If something's off, go back to the chat and tell the AI what to change, then preview again.</p>
            <p className="text-[10px] text-slate-400">Applying opens it in the editor for final tweaks — nothing is saved until you press Save.</p>
          </div>
        )}
      </div>
    </div>
  );
}
